import { BigNumber } from 'bignumber.js';
import { useMemo } from 'react';

import { useAuthorizeSigningRequest } from '@/providers';
import type { ChainKey } from '@infinex/chain-config';
import type { SigningRequest } from '@infinex/connect-service-contract/client';
import { useDataset } from '@infinex/user';
import { useUnifiedBalances } from '@infinex/web-shared/features/balances';
import { calculateFormattedAssetDecimalPrecision } from '@infinex/web-shared/utils';

import type { UserNativeBalanceDetails } from '../../../types';

type UseGasDetailsUserInfoParams = {
  chainKey: ChainKey;
  walletAddress: string;
  selectedSponsorshipMode: string;
};

export function useGasDetailsUserInfo({
  chainKey,
  walletAddress,
  selectedSponsorshipMode,
}: UseGasDetailsUserInfoParams) {
  const { metadata } = useAuthorizeSigningRequest() as {
    metadata: SigningRequest;
  };

  // Fetch ledger balances dataset (contains gas account balance)
  const { data: ledgerBalances, status: ledgerBalancesStatus } =
    useDataset('ledgerBalances');

  const { tokenBalances, isLoading: isBalancesLoading } = useUnifiedBalances({
    includeEoaOnAllEvmChains: true,
  });

  // Find the native token balance for this chain/wallet from unified balances
  const userBalance = useMemo<UserNativeBalanceDetails | undefined>(() => {
    if (!tokenBalances) return undefined;

    const nativeBalance = tokenBalances.find(
      (balance) =>
        balance.chain === chainKey &&
        balance.assetAddress === 'native' &&
        balance.walletAddress === walletAddress
    );

    if (!nativeBalance) return undefined;

    return {
      rawBalance: nativeBalance.rawBalance,
      decimalBalance: nativeBalance.decimalBalance.toNumber(),
      balanceUsd: nativeBalance.balanceUsd ?? null,
      imgUrl: nativeBalance.imgUrl,
      name: nativeBalance.name,
      symbol: nativeBalance.symbol,
      decimals: nativeBalance.decimals,
      chainKey,
      walletAddress,
    };
  }, [tokenBalances, chainKey, walletAddress]);

  // Consider loading complete only when both balances sources have loaded
  const isLoadingBalance =
    isBalancesLoading || ledgerBalancesStatus === 'loading';

  // Extract gas account balance
  const gasAccountBalance = useMemo(() => {
    if (!ledgerBalances) return null;

    const gasBalances = ledgerBalances.ledgerBalances.filter(
      (balance) => balance.category === 'gas'
    );

    if (gasBalances.length === 0) return null;

    // For now, take the first gas balance (assuming single gas denomination)
    const balance = gasBalances[0];
    // The balance field is already in decimal format (USD), no need to divide by decimals
    const decimalBalance = Number(balance.balance);

    return decimalBalance;
  }, [ledgerBalances]);

  // Format gas cost - show "<0.001" if less than 0.001
  const gasCostValue = parseFloat(String(metadata?.quoteGasCostUsd || '0'));
  const formattedGasCost =
    gasCostValue > 0 && gasCostValue < 0.001
      ? '<0.001'
      : gasCostValue.toFixed(3);

  // Use gas rating from metadata
  const isHighGasCost = metadata?.quoteGasRating === 'high';

  // Calculate native balance in USD for comparison
  const nativeBalanceUsd = useMemo(() => {
    return userBalance?.balanceUsd ?? 0;
  }, [userBalance?.balanceUsd]);

  // Calculate proper decimal precision for token balance display
  const formattedNativeBalance = useMemo(() => {
    if (!userBalance) return '0';

    const { decimalBalance, balanceUsd } = userBalance;
    if (decimalBalance === 0) return '0';

    if (!balanceUsd) {
      return decimalBalance.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
      });
    }

    const rateUsd = balanceUsd / decimalBalance;
    const decimals = calculateFormattedAssetDecimalPrecision({
      tokenAmount: decimalBalance.toString(),
      rateUsd,
    });

    return decimalBalance.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  }, [userBalance]);

  // Check if user has sufficient funds based on selected payment method
  // Defaults to false until we confirm the user has enough funds
  const hasSufficientFunds = useMemo(() => {
    // Sponsored transactions don't require user funds
    if (selectedSponsorshipMode === 'sponsored') return true;

    if (isLoadingBalance) return false;

    if (selectedSponsorshipMode === 'account') {
      // Check gas account balance against USD cost
      if (gasAccountBalance === null) return false;
      return gasAccountBalance >= gasCostValue;
    } else {
      // Check native balance against native cost
      if (!metadata?.quoteGasCostNative || !userBalance?.rawBalance) {
        return false;
      }
      const gasCost = new BigNumber(String(metadata.quoteGasCostNative));
      return userBalance.rawBalance.gte(gasCost);
    }
  }, [
    selectedSponsorshipMode,
    gasAccountBalance,
    gasCostValue,
    userBalance?.rawBalance,
    metadata?.quoteGasCostNative,
    isLoadingBalance,
  ]);

  return {
    userBalance,
    isLoadingBalance,
    gasAccountBalance,
    gasCostValue,
    formattedGasCost,
    isHighGasCost,
    nativeBalanceUsd,
    formattedNativeBalance,
    hasSufficientFunds,
  };
}
